import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../atoms/Card';
import OrderDetailModal from '../OrderDetailModal';
import { OrderOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import './BartenderOrderHistoryList.scss';

const PAGE_SIZE = 20;

interface BartenderOrderHistoryListProps {
  eventId?: number;
  eventName?: string;
  eventVenueId?: number;
  className?: string;
}

const BartenderOrderHistoryList: React.FC<BartenderOrderHistoryListProps> = ({
  eventId,
  eventName,
  eventVenueId,
  className = ''
}) => {
  const { t } = useTranslation();
  const { formatPurchaseDateTime } = useDateFormatter();
  const [orders, setOrders] = useState<OrderOut[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderOut | null>(null);

  useEffect(() => {
    setPage(1);
  }, [eventId, eventVenueId]);

  useEffect(() => {
    fetchOrders(page, true);
    const intervalId = window.setInterval(() => fetchOrders(page, false), 15000);
    return () => window.clearInterval(intervalId);
  }, [eventId, eventVenueId, page]);

  const fetchOrders = async (pageToLoad: number, showLoader = false) => {
    if (showLoader) setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        skip: (pageToLoad - 1) * PAGE_SIZE,
        limit: PAGE_SIZE
      };

      if (eventId) {
        params.event_id = eventId;
      }

      if (eventVenueId) {
        params.event_venue_id = eventVenueId;
      }

      const ordersData = await apiService.getOrders(params);

      if (ordersData.length === 0 && pageToLoad > 1) {
        setPage(pageToLoad - 1);
        return;
      }

      setOrders(ordersData);
      setHasNextPage(ordersData.length === PAGE_SIZE);
    } catch (error: any) {
      console.error('Failed to fetch order history:', error);
      setError(t('bartender.orders.fetchError'));
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className={`bartender-order-history ${className}`}>
        <div className="bartender-order-history__loading">
          🔄 {t('bartender.orders.loading')}
        </div>
      </Card>
    );
  }

  return (
    <Card className={`bartender-order-history ${className}`}>
      <div className="bartender-order-history__header">
        <h3 className="bartender-order-history__title">
          {t('bartender.orders.historyTitle')}
        </h3>
      </div>

      {error && (
        <div className="bartender-order-history__error">
          ❌ {error}
        </div>
      )}

      {orders.length === 0 ? (
        <div className="bartender-order-history__empty">
          📭 {t('bartender.orders.noOrders')}
        </div>
      ) : (
        <div className="bartender-order-history__table">
          <div className="bartender-order-history__row bartender-order-history__row--header">
            <div className="bartender-order-history__cell bartender-order-history__cell--id">
              {t('bartender.orders.tableOrderId')}
            </div>
            <div className="bartender-order-history__cell bartender-order-history__cell--items">
              {t('bartender.orders.tableItems')}
            </div>
            <div className="bartender-order-history__cell bartender-order-history__cell--status">
              {t('bartender.orders.tableStatus')}
            </div>
            <div className="bartender-order-history__cell bartender-order-history__cell--time">
              {t('bartender.orders.tableCreated')}
            </div>
          </div>

          {orders.map((order) => (
            <div
              key={order.id}
              className="bartender-order-history__row bartender-order-history__row--clickable"
              onClick={() => setSelectedOrder(order)}
            >
              <div className="bartender-order-history__cell bartender-order-history__cell--id">
                #{order.id}
              </div>
              <div className="bartender-order-history__cell bartender-order-history__cell--items">
                {order.items.map((item, index) => (
                  <span key={index} className="bartender-order-history__item">
                    {item.quantity}x {item.drink_name || item.food_name}
                  </span>
                ))}
              </div>
              <div className="bartender-order-history__cell bartender-order-history__cell--status">
                {t(`orderConfirmation.statuses.${order.status.toLowerCase()}`, { defaultValue: order.status })}
              </div>
              <div className="bartender-order-history__cell bartender-order-history__cell--time">
                {formatPurchaseDateTime(order.created_at).fullDateTime}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bartender-order-history__pagination">
        <button
          type="button"
          className="bartender-order-history__pagination-button"
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page === 1}
        >
          {t('bartender.orders.previous')}
        </button>
        <span className="bartender-order-history__pagination-page">
          {t('bartender.orders.pageNumber', { page })}
        </span>
        <button
          type="button"
          className="bartender-order-history__pagination-button"
          onClick={() => setPage((prev) => prev + 1)}
          disabled={!hasNextPage}
        >
          {t('bartender.orders.next')}
        </button>
      </div>

      <OrderDetailModal
        isOpen={selectedOrder !== null}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        onOrderUpdate={(updatedOrder) => {
          setSelectedOrder(updatedOrder);
          setOrders((prev) => prev.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)));
        }}
        eventName={eventName}
      />
    </Card>
  );
};

export default BartenderOrderHistoryList;
